import React, { useState, useEffect } from "react";
import { AlertCircle, DollarSign, Users, FileText, AlertTriangle, TrendingUp, RefreshCw } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { getFinanceSummary } from "../../services/adminFinance";

const formatCents = (cents) => {
  if (cents === null || cents === undefined) return null;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(cents) / 100);
};

const MetricCard = ({ icon: Icon, label, value, sub, color = "purple" }) => (
  <div className="neumorphic-card rounded-2xl p-4 border border-gray-200 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${color}-500/10`}>
        <Icon size={18} className={`text-${color}-600 dark:text-${color}-400`} />
      </div>
      <div>
        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">{label}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
    </div>
  </div>
);

const Finance = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setError("");
    try { const data = await getFinanceSummary(); setSummary(data); }
    catch (err) { setError(err.message || "Erro ao carregar financeiro"); }
    finally { setLoading(false); }
  };

  const revenue = summary?.revenue?.thisMonthCents;
  const prevRevenue = summary?.revenue?.previousMonthCents;
  const payments = summary?.payments;
  const subs = summary?.subscriptions;
  const inv = summary?.invoices;
  const delinq = summary?.delinquency;

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Comercial e Cobrança"
        title="Financeiro"
        subtitle="Visão financeira global da plataforma"
        action={<button onClick={load} className="p-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-xl text-gray-600 dark:text-gray-400"><RefreshCw size={14} /></button>}
      />

      {error && <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3"><AlertCircle size={16} /> {error}</div>}

      {loading ? (
        <div className="text-center py-12 text-xs text-gray-400">Carregando...</div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard icon={TrendingUp} label="Receita do Mês" value={revenue !== null ? formatCents(revenue) : "Indisponível"} color="emerald" />
            <MetricCard icon={DollarSign} label="Pagamentos (Mês)" value={payments?.thisMonthCount ?? 0} sub={formatCents(payments?.thisMonthAmountCents)} color="blue" />
            <MetricCard icon={FileText} label="Faturas Abertas" value={inv?.open ?? 0} color="amber" />
            <MetricCard icon={AlertTriangle} label="Faturas Vencidas" value={inv?.overdue ?? 0} color="rose" />
            <MetricCard icon={Users} label="Assinaturas Ativas" value={subs?.active ?? 0} sub={`${subs?.total ?? 0} total`} color="purple" />
            <MetricCard icon={AlertTriangle} label="Inadimplentes" value={subs?.pastDue ?? 0} color="rose" />
            <MetricCard icon={Users} label="Trial" value={subs?.trial ?? 0} color="cyan" />
            <MetricCard icon={Users} label="Canceladas" value={subs?.cancelled ?? 0} color="gray" />
          </div>

          <div className="neumorphic-card rounded-2xl p-4 border border-gray-200 dark:border-white/10">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white mb-3">Indicadores de Inadimplência</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-gray-500">Empresas inadimplentes:</span> <span className="font-bold text-rose-600">{delinq?.companies ?? 0}</span></div>
              <div><span className="text-gray-500">Valor inadimplido:</span> <span className="font-bold">{delinq?.amountCents !== null ? formatCents(delinq?.amountCents) : "Indisponível"}</span></div>
            </div>
          </div>

          <div className="neumorphic-card rounded-2xl p-4 border border-gray-200 dark:border-white/10">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white mb-3">Status Integração</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><span className="text-gray-500">Faturas pagas:</span> <span className="font-bold text-emerald-600">{inv?.paid ?? 0}</span></div>
              <div><span className="text-gray-500">Faturas canceladas:</span> <span className="font-bold">{inv?.cancelled ?? 0}</span></div>
              <div><span className="text-gray-500">Receita mês anterior:</span> <span>{prevRevenue !== null ? formatCents(prevRevenue) : "Indisponível"}</span></div>
              <div><span className="text-gray-500">Gerado em:</span> <span>{summary.generatedAt ? new Date(summary.generatedAt).toLocaleString("pt-BR") : "—"}</span></div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Finance;
